import React, { useEffect, useMemo, useState } from 'react';
import { Box, Typography } from '@mui/material';
import { Link as RouterLink } from 'react-router-dom';
import useCycleClockData from './useCycleClockData';
import ClockDial from './ClockDial';
import MarketRead from './MarketRead';
import RankedList from './RankedList';
import { tok, LABEL } from './clockUi';
import { readClockScope, saveClockScope, useClockUniverse } from './clockScope';
import { depthCounts, matchesDepth, readDepthFilter, saveDepthFilter } from './clockDepthFilter';
import { matchesStages, readStageFilter, saveStageFilter, stageCounts } from './clockStageFilter';
import { FilterPanel, FilterSummary, SideSwitch, describeFilters } from './ClockFilters';
import { readFiltersOpen, saveFiltersOpen } from './clockFilterState';
import { readClockSide, saveClockSide, sideOf } from './clockSide';

// Markets as a Cycle Clock (DWLF-301; design canvas board AppCycleMap): the dial, the focused
// market's read and the ranked list, filtered by stage and depth, for the weekly lows or highs (DWLF-552).

// The dial's drawn size: up to its full 640px, never taller than the window leaves below the navbar (64), this
// page's top padding (24), the title block (59), the filter summary wrapped under it (52) and the gap (16),
// rounded up, so the whole dial opens above the fold on a laptop; never below 360px. Change it with the header.
const DIAL_FIT = 'clamp(360px, calc(100vh - 230px), 640px)';

// The focused market is one the filters keep: the hovered market, else the selected one, else the first ranked
// match. One a filter excludes is passed over, and when nothing matches nothing is focused (the read pane never
// shows an excluded market).
const focusOf = ({ markets, ranked, inFilter, symbols }) => {
  const kept = (m) => m && (!m.onDial || inFilter(m));
  const picked = symbols.map((sym) => markets.find((m) => m.symbol === sym)).find(kept);
  return picked || ranked.find(inFilter) || null;
};

export default function CycleClockPage() {
  const [scope, setScope] = useState(readClockScope);
  const universe = useClockUniverse(scope);
  const { groups, groupsFailed } = universe;
  const chooseScope = (next) => { setScope(next); saveClockScope(next); };
  // A remembered group the API no longer lists opens the watchlist instead of a failed read. Not saved, so
  // a failed groups read (said above the clock) does not forget the choice.
  useEffect(() => {
    if (groups && scope.kind === 'group' && !groups.some((g) => g.name === scope.group)) setScope({ kind: 'watchlist' });
  }, [groups, scope]);
  const onScope = (id) => {
    if (id !== 'group') { chooseScope({ kind: id }); return; }
    const group = scope.kind === 'group' ? scope.group : (groups?.[0]?.name);
    if (group) chooseScope({ kind: 'group', group });
  };

  const [sideId, setSideId] = useState(readClockSide);
  const side = sideOf(sideId);
  const chooseSide = (id) => { setSideId(id); saveClockSide(id); };
  const clock = useCycleClockData({ depth: true, symbols: universe.symbols, side: side.id });
  const { loading, empty, partial, screenerMissing, markets, verification } = clock;
  const error = universe.failed || clock.error;
  const { untracked } = universe;
  const [stages, setStages] = useState(readStageFilter);
  const chooseStages = (ids) => { setStages(ids); saveStageFilter(ids); };
  const [depths, setDepths] = useState(readDepthFilter);
  const chooseDepths = (ids) => { setDepths(ids); saveDepthFilter(ids); };
  const [filtersOpen, setFiltersOpen] = useState(readFiltersOpen);
  const toggleFilters = () => { setFiltersOpen(!filtersOpen); saveFiltersOpen(!filtersOpen); };
  const [selected, setSelected] = useState(null);
  const [hover, setHover] = useState(null);

  const onDial = useMemo(() => markets.filter((m) => m.onDial), [markets]);
  const offDial = useMemo(() => markets.filter((m) => !m.onDial), [markets]);
  const ranked = useMemo(() => [...onDial].sort((a, b) => b.p - a.p), [onDial]);
  const inStage = (m) => matchesStages(stages, m);
  const inDepth = (m) => matchesDepth(depths, m);
  // Stage AND depth, on the dial and the ranked list; each filter's counts are of on-dial markets the other keeps.
  const inFilter = (m) => inStage(m) && inDepth(m);
  const counts = depthCounts(onDial.filter(inStage));
  const stageCountsKept = stageCounts(onDial.filter(inDepth));
  const shown = ranked.filter(inFilter).length;
  const noneMatch = ranked.length > 0 && shown === 0;

  const focus = focusOf({ markets, ranked, inFilter, symbols: [hover, selected] });

  return (
    <Box sx={{ px: { xs: 2, md: 4 }, py: 3, maxWidth: 1440, mx: 'auto' }}>
      <Box sx={{ display: 'flex', alignItems: 'flex-end', justifyContent: 'space-between', flexWrap: 'wrap', gap: 2, mb: 2 }}>
        <Box>
          <Typography sx={{ ...LABEL, fontSize: '0.6875rem', color: tok('accent') }}>
            Markets · cycle clock{verification?.state === 'unverified' && ' · windows unverified'}
          </Typography>
          <Typography component="h1" sx={{ fontFamily: '"Space Grotesk", sans-serif', fontWeight: 600, fontSize: { xs: '1.5rem', md: '1.75rem' }, letterSpacing: '-0.02em', color: tok('text') }}>
            Where every market sits in its weekly cycle
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, flexWrap: 'wrap', minWidth: 0 }}>
          <SideSwitch side={side} onSide={chooseSide} />
          <FilterSummary summary={describeFilters({ side, scope, stages, depths, shown, total: ranked.length })} open={filtersOpen} onToggle={toggleFilters} />
        </Box>
      </Box>

      {filtersOpen && (
        <FilterPanel
          scope={{ scope, groups, onScope, onGroup: (group) => chooseScope({ kind: 'group', group }) }}
          stage={{ stages, counts: stageCountsKept, onStages: chooseStages }}
          depth={{ depths, counts, onDepths: chooseDepths }}
        />
      )}
      {error && <Typography sx={{ color: tok('down'), mb: 2 }}>{error}</Typography>}
      {groupsFailed && <Typography sx={{ fontSize: '0.75rem', color: tok('warn'), mb: 2 }}>Groups unavailable.</Typography>}
      {empty && scope.kind === 'watchlist' && (
        <Typography sx={{ fontSize: '0.875rem', color: tok('ink-60'), mb: 2 }}>
          Your watchlist is empty. Add markets from a chart or the{' '}
          <RouterLink to="/markets/overview" style={{ color: 'var(--dwlf-accent)' }}>overview</RouterLink> and they appear on the clock.
        </Typography>
      )}
      {empty && !error && scope.kind !== 'watchlist' && (
        <Typography sx={{ fontSize: '0.875rem', color: tok('ink-60'), mb: 2 }}>No tracked markets in this view.</Typography>
      )}
      {untracked?.length > 0 && (
        <Typography sx={{ fontSize: '0.75rem', color: tok('ink-55'), mb: 2 }}>Not tracked, so not shown: {untracked.join(', ')}.</Typography>
      )}
      {(partial > 0 || screenerMissing) && (
        <Typography sx={{ fontSize: '0.75rem', color: tok('warn'), mb: 2 }}>
          {partial > 0 && `${partial} market(s) failed to load. `}{screenerMissing && 'Screener stages unavailable; showing timing only.'}
        </Typography>
      )}

      <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', lg: 'auto minmax(0, 1fr)' }, gap: { xs: 3, lg: 5 }, alignItems: 'start' }}>
        <ClockDial size={DIAL_FIT} side={side} onDial={onDial} focus={focus} inFilter={inFilter} loading={loading} onSelect={setSelected} onHover={setHover} />

        {/* Read + list */}
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3, minWidth: 0 }}>
          <MarketRead focus={focus} />
          {noneMatch && <Typography sx={{ fontSize: '0.75rem', color: tok('ink-55') }}>No markets on the dial match these filters.</Typography>}
          <RankedList side={side} ranked={ranked} offDial={offDial} focus={focus} inFilter={inFilter} onSelect={setSelected} />
        </Box>
      </Box>

      {/* The standing caveats sit under the clock, not above it, so the dial opens above the fold. */}
      <Box sx={{ mt: 3, display: 'flex', flexDirection: 'column', gap: 1, maxWidth: 900 }}>
        {verification?.state === 'unverified' && (
          <Typography sx={{ fontSize: '0.75rem', lineHeight: 1.6, color: tok('ink-55'), border: `1px dashed ${tok('ink-15')}`, px: 1.5, py: 1 }}>
            Windows are drawn from each market's own past gaps and are <strong>unverified</strong>: no claim is made that the next {side.pivot} lands inside one.
            Position = time since the last confirmed weekly {side.pivot} ÷ the typical gap.
          </Typography>
        )}
        {depths.length > 0 && (
          <Typography sx={{ fontSize: '0.75rem', color: tok('ink-55') }}>
            Depth places the last weekly close in the band of past cycles' moves: historical context, not a target.
          </Typography>
        )}
      </Box>
    </Box>
  );
}
